/**
 * Feature Extractor for URL Phishing Detection
 * Extracts 20+ features from URLs for ML classification
 */

export class FeatureExtractor {
  constructor() {
    this.suspiciousKeywords = [
      'login', 'signin', 'verify', 'secure', 'account', 'update', 'confirm',
      'banking', 'password', 'credential', 'suspend', 'unusual', 'alert',
      'urgent', 'immediate', 'expire', 'limited', 'click', 'here', 'now'
    ];

    this.trustedDomains = [
      'google.com', 'facebook.com', 'amazon.com', 'microsoft.com', 'apple.com',
      'paypal.com', 'netflix.com', 'twitter.com', 'linkedin.com', 'github.com',
      'youtube.com', 'instagram.com', 'whatsapp.com', 'zoom.us', 'dropbox.com'
    ];

    this.suspiciousTLDs = [
      '.tk', '.ml', '.ga', '.cf', '.gq', '.xyz', '.top', '.work', '.click',
      '.link', '.info', '.online', '.site', '.website', '.space', '.pw'
    ];
  }

  /**
   * Extract all features from a URL
   * @param {string} url - The URL to analyze
   * @returns {Object} - Feature object with all extracted features
   */
  extractFeatures(url) {
    try {
      // Normalize URL
      let normalizedUrl = url.trim();
      if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
        normalizedUrl = 'https://' + normalizedUrl;
      }

      const urlObj = new URL(normalizedUrl);
      const hostname = urlObj.hostname.toLowerCase();
      const pathname = urlObj.pathname;
      const fullUrl = normalizedUrl.toLowerCase();

      const features = {
        // Length-based features
        urlLength: fullUrl.length,
        hostnameLength: hostname.length,
        pathLength: pathname.length,

        // Character-based features
        numDots: (fullUrl.match(/\./g) || []).length,
        numHyphens: (fullUrl.match(/-/g) || []).length,
        numUnderscores: (fullUrl.match(/_/g) || []).length,
        numSlashes: (fullUrl.match(/\//g) || []).length,
        numDigits: (fullUrl.match(/\d/g) || []).length,
        numSpecialChars: (fullUrl.match(/[@#$%^&*()+=\[\]{}|\\:;"'<>,?]/g) || []).length,
        numSubdomains: hostname.split('.').length - 2,

        // Protocol features
        hasHttps: urlObj.protocol === 'https:' ? 1 : 0,
        hasWww: hostname.startsWith('www.') ? 1 : 0,

        // IP-based features
        hasIPAddress: this.hasIPAddress(hostname) ? 1 : 0,

        // Suspicious patterns
        hasSuspiciousKeywords: this.hasSuspiciousKeywords(fullUrl) ? 1 : 0,
        numSuspiciousKeywords: this.countSuspiciousKeywords(fullUrl),
        hasSuspiciousTLD: this.hasSuspiciousTLD(hostname) ? 1 : 0,

        // Domain features
        domainAge: this.estimateDomainAge(hostname),
        isTrustedDomain: this.isTrustedDomain(hostname) ? 1 : 0,

        // URL structure features
        hasAtSymbol: fullUrl.includes('@') ? 1 : 0,
        hasDoubleSlash: pathname.includes('//') ? 1 : 0,
        hasPortNumber: urlObj.port ? 1 : 0,

        // Query string features
        hasQueryString: urlObj.search.length > 0 ? 1 : 0,
        queryLength: urlObj.search.length,
        numQueryParams: urlObj.searchParams.toString().split('&').filter(p => p).length,

        // Entropy (randomness) of URL
        urlEntropy: this.calculateEntropy(fullUrl),

        // Brand impersonation
        hasBrandInSubdomain: this.hasBrandInSubdomain(hostname) ? 1 : 0,

        // Shortening service detection
        isShortened: this.isShortened(hostname) ? 1 : 0,

        // Punycode detection (internationalized domain names)
        hasPunycode: hostname.includes('xn--') ? 1 : 0,

        // File extension in URL
        hasSuspiciousExtension: this.hasSuspiciousExtension(pathname) ? 1 : 0
      };

      return {
        features,
        featureVector: this.toVector(features),
        metadata: {
          url: normalizedUrl,
          hostname,
          pathname,
          protocol: urlObj.protocol
        }
      };
    } catch (error) {
      console.error('Error extracting features:', error);
      return null;
    }
  }

  /**
   * Convert features object to array for ML model
   */
  toVector(features) {
    return [
      features.urlLength,
      features.hostnameLength,
      features.pathLength,
      features.numDots,
      features.numHyphens,
      features.numUnderscores,
      features.numSlashes,
      features.numDigits,
      features.numSpecialChars,
      features.numSubdomains,
      features.hasHttps,
      features.hasWww,
      features.hasIPAddress,
      features.hasSuspiciousKeywords,
      features.numSuspiciousKeywords,
      features.hasSuspiciousTLD,
      features.domainAge,
      features.isTrustedDomain,
      features.hasAtSymbol,
      features.hasDoubleSlash,
      features.hasPortNumber,
      features.hasQueryString,
      features.queryLength,
      features.numQueryParams,
      features.urlEntropy,
      features.hasBrandInSubdomain,
      features.isShortened,
      features.hasPunycode,
      features.hasSuspiciousExtension
    ];
  }

  hasIPAddress(hostname) {
    const ipv4Pattern = /^(\d{1,3}\.){3}\d{1,3}$/;
    const ipv6Pattern = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
    return ipv4Pattern.test(hostname) || ipv6Pattern.test(hostname);
  }

  hasSuspiciousKeywords(url) {
    return this.suspiciousKeywords.some(keyword => url.includes(keyword));
  }

  countSuspiciousKeywords(url) {
    return this.suspiciousKeywords.filter(keyword => url.includes(keyword)).length;
  }

  hasSuspiciousTLD(hostname) {
    return this.suspiciousTLDs.some(tld => hostname.endsWith(tld));
  }

  estimateDomainAge(hostname) {
    // Simplified: trusted domains get higher score
    if (this.isTrustedDomain(hostname)) return 10;
    // Random domains get lower score
    return Math.random() * 5;
  }

  isTrustedDomain(hostname) {
    return this.trustedDomains.some(domain => 
      hostname === domain || hostname.endsWith('.' + domain)
    );
  }

  calculateEntropy(str) {
    const freq = {};
    for (const char of str) {
      freq[char] = (freq[char] || 0) + 1;
    }
    let entropy = 0;
    const len = str.length;
    for (const char in freq) {
      const p = freq[char] / len;
      entropy -= p * Math.log2(p);
    }
    return Math.round(entropy * 100) / 100;
  }

  hasBrandInSubdomain(hostname) {
    const brands = ['google', 'facebook', 'amazon', 'microsoft', 'apple', 'paypal', 'netflix', 'bank'];
    const parts = hostname.split('.');
    if (parts.length <= 2) return false;
    const subdomain = parts.slice(0, -2).join('.');
    return brands.some(brand => subdomain.includes(brand));
  }

  isShortened(hostname) {
    const shorteners = ['bit.ly', 'tinyurl.com', 't.co', 'goo.gl', 'ow.ly', 'is.gd', 'buff.ly', 'adf.ly', 'j.mp'];
    return shorteners.some(s => hostname.includes(s));
  }

  hasSuspiciousExtension(pathname) {
    const suspiciousExts = ['.exe', '.zip', '.rar', '.js', '.php', '.asp', '.scr', '.bat', '.cmd'];
    return suspiciousExts.some(ext => pathname.toLowerCase().endsWith(ext));
  }

  /**
   * Get human-readable feature descriptions
   */
  getFeatureDescriptions(features) {
    const descriptions = [];

    if (features.hasHttps === 0) {
      descriptions.push({ name: 'No HTTPS', value: 'Connection not secure', risk: 'negative' });
    } else {
      descriptions.push({ name: 'HTTPS Enabled', value: 'Secure connection', risk: 'positive' });
    }

    if (features.hasIPAddress === 1) {
      descriptions.push({ name: 'IP Address URL', value: 'Uses IP instead of domain', risk: 'negative' });
    }

    if (features.hasSuspiciousKeywords === 1) {
      descriptions.push({ name: 'Suspicious Keywords', value: `Found ${features.numSuspiciousKeywords} keywords`, risk: 'negative' });
    }

    if (features.hasSuspiciousTLD === 1) {
      descriptions.push({ name: 'Suspicious TLD', value: 'Uses risky domain extension', risk: 'negative' });
    }

    if (features.isTrustedDomain === 1) {
      descriptions.push({ name: 'Trusted Domain', value: 'Known legitimate website', risk: 'positive' });
    }

    if (features.urlLength > 75) {
      descriptions.push({ name: 'Long URL', value: `${features.urlLength} characters`, risk: 'negative' });
    }

    if (features.numSubdomains > 3) {
      descriptions.push({ name: 'Many Subdomains', value: `${features.numSubdomains} subdomains`, risk: 'negative' });
    }

    if (features.hasAtSymbol === 1) {
      descriptions.push({ name: '@ Symbol in URL', value: 'Potential redirect trick', risk: 'negative' });
    }

    if (features.hasBrandInSubdomain === 1) {
      descriptions.push({ name: 'Brand Impersonation', value: 'Brand name in subdomain', risk: 'negative' });
    }

    if (features.isShortened === 1) {
      descriptions.push({ name: 'URL Shortener', value: 'Destination hidden', risk: 'negative' });
    }

    if (features.urlEntropy > 4.5) {
      descriptions.push({ name: 'High Randomness', value: 'URL appears randomly generated', risk: 'negative' });
    }

    if (features.hasPunycode === 1) {
      descriptions.push({ name: 'Punycode Domain', value: 'Internationalized domain', risk: 'negative' });
    }

    if (features.hasSuspiciousExtension === 1) {
      descriptions.push({ name: 'Suspicious File', value: 'Potentially dangerous file type', risk: 'negative' });
    }

    // Add some positive indicators if nothing negative found
    if (descriptions.filter(d => d.risk === 'negative').length === 0) {
      descriptions.push({ name: 'Clean URL Structure', value: 'No suspicious patterns', risk: 'positive' });
    }

    if (features.numDots <= 3 && features.numSubdomains <= 1) {
      descriptions.push({ name: 'Simple Domain', value: 'Standard domain structure', risk: 'positive' });
    }

    return descriptions;
  }
}

export const featureExtractor = new FeatureExtractor();
